/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { InsightsFeedResponse as ApiInsightResponse } from '@atlassian/rovo-insights-service-api/types';

import { CopyButton } from './copy-button';
import debugMessages from './messages';
import type { InsightGroupDebugInfo } from './types';

const styles = cssMap({
	container: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		marginBlockEnd: token('space.200'),
	},
	label: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.semibold'),
		color: token('color.text.subtlest'),
	},
	summaryText: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
	},
	errorText: {
		font: token('font.body.small'),
		color: token('color.text.danger'),
	},
});

function formatDuration(secs: number): string {
	if (secs < 60) {
		return `${secs}s`;
	}
	const mins = Math.floor(secs / 60);
	const remainSecs = secs % 60;
	return `${mins}m ${remainSecs}s`;
}

interface DebugInsightItem {
	id: string;
	title: string;
	count: number;
	debugInfo?: InsightGroupDebugInfo;
	subItems?: Array<{ title: string; description: string; thinking?: string }>;
}

function buildDebugReport(items: DebugInsightItem[]): string {
	const lines: string[] = ['# Rovo Insights Debug Report', ''];

	for (const item of items) {
		if (!item.debugInfo) {
			continue;
		}
		const di = item.debugInfo;
		lines.push(`## ${item.title}`);
		lines.push(`- Insights: ${item.count}`);
		lines.push(`- Duration: ${formatDuration(di.durationSecs)}`);
		lines.push(`- Attempts: ${di.attempts}`);
		lines.push(`- Error: ${di.error ?? 'None'}`);
		lines.push(`- Generated: ${di.generatedAt}`);
		lines.push('');

		if (item.subItems) {
			for (const sub of item.subItems) {
				lines.push(`### ${sub.title}`);
				lines.push(sub.description);
				if (sub.thinking) {
					lines.push('');
					lines.push('**AI Thinking:**');
					lines.push(sub.thinking);
				}
				lines.push('');
			}
		}
	}
	return lines.join('\n');
}

interface DebugSummaryBarProps {
	items: DebugInsightItem[];
	rawResponse: ApiInsightResponse | null;
}

/**
 * Feed-level debug summary bar showing overall stats, per-group metrics,
 * and copy buttons for full debug report and raw response.
 */
export const DebugSummaryBar = ({ items, rawResponse }: DebugSummaryBarProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const debugItems = items.filter((item) => item.debugInfo);

	const totalDuration = debugItems.reduce(
		(sum, item) => sum + (item.debugInfo?.durationSecs ?? 0),
		0,
	);
	const totalInsights = debugItems.reduce((sum, item) => sum + item.count, 0);
	const errorCount = debugItems.filter((item) => item.debugInfo?.error).length;

	const longestTask = debugItems.reduce(
		(max, item) => {
			const dur = item.debugInfo?.durationSecs ?? 0;
			return dur > max.duration ? { title: item.title, duration: dur } : max;
		},
		{ title: '', duration: 0 },
	);

	const debugReport = useMemo(() => buildDebugReport(items), [items]);
	const rawJson = useMemo(
		() => (rawResponse ? JSON.stringify(rawResponse, null, 2) : ''),
		[rawResponse],
	);

	return (
		<Box xcss={styles.container}>
			<Stack space="space.075">
				{/* Summary line */}
				<Flex alignItems="center" gap="space.100" wrap="wrap">
					<Box as="span" xcss={styles.label}>
						{formatMessage(debugMessages.debugLabel)}
					</Box>
					<Box as="span" xcss={styles.summaryText}>
						{longestTask.title
							? formatMessage(debugMessages.summaryLineWithLongest, {
									groups: formatMessage(debugMessages.groupsCount, {
										count: debugItems.length,
									}),
									insights: formatMessage(debugMessages.insightsCount, {
										count: totalInsights,
									}),
									duration: formatMessage(debugMessages.totalDuration, {
										duration: formatDuration(totalDuration),
									}),
									longest: formatMessage(debugMessages.longestTask, {
										title: longestTask.title,
										duration: formatDuration(longestTask.duration),
									}),
								})
							: formatMessage(debugMessages.summaryLine, {
									groups: formatMessage(debugMessages.groupsCount, {
										count: debugItems.length,
									}),
									insights: formatMessage(debugMessages.insightsCount, {
										count: totalInsights,
									}),
									duration: formatMessage(debugMessages.totalDuration, {
										duration: formatDuration(totalDuration),
									}),
								})}
					</Box>
					{errorCount > 0 ? (
						<Box as="span" xcss={styles.errorText}>
							{formatMessage(debugMessages.totalErrors, { count: errorCount })}
						</Box>
					) : null}
				</Flex>

				{/* Copy buttons */}
				<Flex gap="space.100" wrap="wrap">
					<CopyButton text={debugReport} label={formatMessage(debugMessages.copyDebugReport)} />
					<CopyButton text={rawJson} label={formatMessage(debugMessages.copyRawResponse)} />
				</Flex>
			</Stack>
		</Box>
	);
};
